import axios from 'axios';
import { baseURL } from '../Constants';

interface MatchLiveState {
  match_id: string;
  get5_match_id?: number;
  status: string;
  map_number: number;
  map_name: string;
  round_number: number;
  team1_id?: number;
  team1_name: string;
  team1_score: number;
  team1_series_score: number;
  team1_side?: string;
  team2_id?: number;
  team2_name: string;
  team2_score: number;
  team2_series_score: number;
  team2_side?: string;
  updated_at: string;
  hidden?: boolean;
}

interface LiveMatchesResponse {
  count: number;
  matches: MatchLiveState[];
  timestamp: string;
}

interface MapStatus {
  map_number: number;
  map_name: string;
  get5_match_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface MapStatusResponse {
  matchId: string;
  maps: MapStatus[];
  totalMaps: number;
  completedMaps: number;
  liveMaps: number;
  pendingMaps: number;
}

class LiveMatchService {
  /**
   * Get all currently live matches from the server
   * Uses the public /api/match/live endpoint - single efficient query
   * No authentication required
   */
  async getAllLiveMatches(): Promise<MatchLiveState[]> {
    try {
      const response = await axios.get<LiveMatchesResponse>(
        `${baseURL}/match/live`,
        {
          timeout: 5000
        }
      );

      return response.data.matches || [];
    } catch (error: any) {
      // Silently fail - endpoint may not be available or no live matches
      return [];
    }
  }

  /**
   * Get just the challonge IDs of currently live matches
   * Useful for quick filtering
   */
  async getLiveMatchChallongeIds(): Promise<string[]> {
    const liveMatches = await this.getAllLiveMatches();
    return liveMatches.map(match => match.match_id);
  }

  /**
   * Get live state for a specific match
   * Returns null if match is not live or error occurs
   */
  async getMatchLiveState(challongeId: string): Promise<MatchLiveState | null> {
    try {
      const response = await axios.get<MatchLiveState>(
        `${baseURL}/match/${challongeId}/live-state`,
        {
          timeout: 3000
        }
      );

      if (response.data.status === 'finished' || response.data.status === 'pending') {
        return null;
      }

      return response.data;
    } catch (error: any) {
      return null;
    }
  }

  /**
   * Get map status for a specific match
   * Returns map list and counts - used to determine BO1 vs BO3
   */
  async getMatchMapStatus(challongeId: string): Promise<MapStatusResponse | null> {
    try {
      const response = await axios.get<MapStatusResponse>(
        `${baseURL}/match/${challongeId}/map-status`,
        {
          timeout: 3000
        }
      );

      return response.data;
    } catch (error: any) {
      return null;
    }
  }
}

export const liveMatchService = new LiveMatchService();
export type { MatchLiveState, MapStatus, MapStatusResponse };
